import { Typography } from '@mui/material';
import type { FormikProps, FormikValues } from 'formik';
import { useRef, useState, type FC } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import { FormikForm } from '../../../components/Formik';
import { ODSTextField } from '../../../components/InputFields';
import Modal from '../../../components/Modal';
import { isValidPassword } from '../../../helpers/validation';
import useApiQuery from '../../../hooks/useApiQuery';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';

const ApiKeyForm: FC = () => {
  const intl = useIntl();
  const formRef = useRef<FormikProps<FormikValues> | null>(null);
  const copyToClipboard = useCopyToClipboard();

  const [apiKey, setApiKey] = useState<string | null>(null);

  const { makeApiCall: createApiKey } = useApiQuery<{ apiKey: string }>(
    Auth.createApiKey as unknown as (args: unknown) => Promise<{ apiKey: string }>,
    {
      errorMessage: 'apiKeyGenerationErr',
    }
  );

  const fields = [
    {
      fieldType: 'password',
      name: 'password',
      label: 'Password',
    },
  ];

  const initialValues = {
    password: '',
  };

  const validationSchema = Yup.object({
    password: Yup.string()
      .test('Strong password', intl.formatMessage({ id: 'ERR_PASSWORD_INVALID' }), value =>
        value ? isValidPassword(value) : true
      )
      .required('Required'),
  });

  const handleSubmit = async (values: Record<string, unknown>) => {
    const { password } = values as FormikValues;

    try {
      const data = (await createApiKey(password)) as { apiKey: string };

      setApiKey(data.apiKey);
      formRef.current?.resetForm();
    } catch (error) {
      console.error('Error generating API key:', error);
      return;
    }
  };

  return (
    <>
      {apiKey && (
        <Modal
          title='Your API key is ready'
          onSubmit={() => {
            copyToClipboard(apiKey);
          }}
          onClose={() => {
            setApiKey(null);
          }}
          submitLabel='Copy'
        >
          <Typography
            variant='body1'
            gutterBottom
          >
            {intl.formatMessage({ id: 'APIkeyCopy1' })}
          </Typography>
          <Typography
            variant='body1'
            gutterBottom
          >
            {intl.formatMessage({ id: 'APIkeyCopy2' })}
          </Typography>
          <ODSTextField
            label='API key'
            name='APIkey'
            value={apiKey}
            InputProps={{ readOnly: true }}
          />
        </Modal>
      )}
      <FormikForm
        formRef={formRef}
        fields={fields}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          void handleSubmit(values);
        }}
        submitLabel='Generate API key'
      />
    </>
  );
};

export default ApiKeyForm;
